import React, { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { asyncRouteLoader } from 'utils/asyncRouteLoader';
import { ProtectedRouteGuard } from 'guard/ProtectedRouteGuard';
import { SuspenseOutlet } from 'utils/routing/SuspenseOutlet';

const Login = asyncRouteLoader(
  () => import('containers/Login'),
  [
    {
      type: 'saga',
      importPromise: () => import('containers/Login/sagas'),
    },
  ]
);
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const LoginGuard = lazy(() => import('containers/Login/LoginGuard'));
const Logout = lazy(() => import('containers/Logout'));
const SignUp = lazy(() => import('pages/sign-up'));
const SignUpWelcome = lazy(() => import('pages/sign-up/welcome'));
const PasswordForm = lazy(() => import('containers/PasswordForm'));
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const PendingLogin = lazy(() => import('pages/pendingLogin'));
export const NotFoundPage = lazy(() => import('containers/NotFoundPage'));
const NotAuthorized = lazy(() => import('containers/NotAuthorized'));
const SSOTestSuccessful = lazy(() => import('pages/app/users/sso/success'));
const SSOTestFailed = lazy(() => import('pages/app/users/sso/failed'));
const PublicLanding = asyncRouteLoader(
  () => import('containers/PublicLanding'),
  [
    {
      type: 'reducer',
      reducerKey: 'publicLanding',
      importPromise: () => import('containers/PublicLanding/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/PublicLanding/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'publicAssessment',
      importPromise: () => import('containers/PublicAssessment/reducer'),
    },
  ]
);
const PublicAssessment = asyncRouteLoader(
  () => import('containers/PublicAssessment'),
  [
    {
      type: 'reducer',
      reducerKey: 'publicAssessment',
      importPromise: () => import('containers/PublicAssessment/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/PublicAssessment/sagas'),
    },
    {
      type: 'reducer',
      reducerKey: 'home',
      importPromise: () => import('containers/Home/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'team',
      importPromise: () => import('containers/Team/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'transcript',
      importPromise: () => import('containers/Transcript/reducer'),
    },
    {
      type: 'reducer',
      reducerKey: 'projectView',
      importPromise: () => import('containers/ProjectView/reducer'),
    },
    {
      type: 'saga',
      importPromise: () => import('containers/ProjectView/sagas'),
    },
  ]
);
const PublicWorkflowAction = lazy(
  () => import('containers/PublicAssessment/WorkflowAction')
);
const SlackInstall = lazy(() => import('containers/Slack/install'));
const SlackOauthRedirect = lazy(() => import('containers/Slack/oauthRedirect'));
const PreviewWorkflowMessage = lazy(
  () => import('pages/app/previewWorkflowMessage')
);

const IroncladOAuthRedirect = lazy(
  () => import('containers/Ironclad/OAuthRedirect')
);
const DocuSignReturn = lazy(() => import('pages/runtime/docusign-return'));

export const indexRoutes = () => (
  <>
    <Route
      path="login"
      element={
        <LoginGuard>
          <Login />
        </LoginGuard>
      }
    />
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <Route path="logout" element={<Logout />} />
    <Route path="sign-up" element={<SignUp />} />
    <Route path="sign-up/welcome" element={<SignUpWelcome />} />
    <Route path="password/:token" element={<PasswordForm />} />
    <Route path="pendingLogin" element={<PendingLogin />} />
    <Route path="loginsso/*" element={<Navigate to="/app" replace />} />
    <Route path="sso-test-successful" element={<SSOTestSuccessful />} />
    <Route path="sso-test-failed" element={<SSOTestFailed />} />
    <Route path="pubt/:pubTeamUuid" element={<PublicLanding />} />
    <Route path="pubt/:pubTeamUuid/:create" element={<PublicLanding />} />
    <Route path="puba/:pubAssessmentUuid" element={<PublicAssessment />} />
    <Route
      path="puba/:pubAssessmentUuid/branch/:branchUUID"
      element={<PublicAssessment />}
    />
    <Route
      path="pubawa/:assessmentID/page/:pageUUID/:action"
      element={
        <ProtectedRouteGuard>
          <PublicWorkflowAction />
        </ProtectedRouteGuard>
      }
    />
    <Route
      path="puba/:appID/page/:pageUUID/preview-workflow-message/:type"
      element={
        <ProtectedRouteGuard>
          <PreviewWorkflowMessage />
        </ProtectedRouteGuard>
      }
    />
    <Route path="forbidden" element={<NotAuthorized />} />
    <Route path="404" element={<NotFoundPage />} />
    {/* DocuSign return page - public route for redirect handling */}
    <Route path="runtime/docusign/return" element={<DocuSignReturn />} />

    {/* Base path: / - protected Slack routes */}
    <Route
      path="slack"
      element={
        <ProtectedRouteGuard>
          <SuspenseOutlet />
        </ProtectedRouteGuard>
      }
    >
      <Route path="install" element={<SlackInstall />} />
      <Route path="oauth-redirect" element={<SlackOauthRedirect />} />
    </Route>

    <Route
      path="ironclad"
      element={
        <ProtectedRouteGuard>
          <SuspenseOutlet />
        </ProtectedRouteGuard>
      }
    ></Route>
    <Route
      path="/ironclad/oauth-redirect"
      element={<IroncladOAuthRedirect />}
    />
  </>
);
